import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { SharedService } from '../../../shared/shared.service';
import { Country } from '../../../shared/types/country.model';
import { ProfileService } from '../../profile.service';
import { User } from '../../types/user.model';

@Component({
  selector: 'mmb-web-app-add-displayed-location-modal',
  templateUrl: './add-displayed-location-modal.component.html',
  styleUrls: ['./add-displayed-location-modal.component.scss']
})
export class AddDisplayedLocationModalComponent implements OnInit {
  @Output() addSuccess: EventEmitter<any> = new EventEmitter<any>();
  @Output() closed: EventEmitter<any> = new EventEmitter<any>();
  @Input() user: User;

  form: UntypedFormGroup;
  currentCountriesSearch: any;
  currentCitiesSearch: any;

  constructor(private fb: UntypedFormBuilder,
    private sharedService: SharedService,
    private profileService: ProfileService,
    private toastr: ToastrService) { }

  ngOnInit() {
    this.form = this.fb.group({
      currentCountry: [this.user.country_current, Validators.required],
      currentCity: [this.user.city_current ? this.user.city_current : this.user.city_custom_current, Validators.required]
    });
    this._subscribeToCurrentCountryValueChanges();
    this._subscribeToCurrentCityValueChanges();
  }

  onClose() {
    this.closed.emit();
  }

  /**
   * Focus event for autocomplete
   * @param e 
   */
  public onFocus(e: Event): void {
    e.stopPropagation();

    setTimeout(() => {
      const inputEvent: Event = new Event('input');
      e.target.dispatchEvent(inputEvent);
    }, 0);
  }

  /**
   * Used to format the result data from the lookup into the
   * display and list values. Maps `{name: "country", id:"id" }` into a string
   */
  resultFormatCountryValue(value: any) {
    if (value.name_ascii) {
      return value.name_ascii;
    }
    return value.name;
  }


  /**
   * Initially binds the string value and then after selecting
   * an item by checking either for string or key/value object.
   */
  inputFormatCountryValue(value: any) {
    if (value.name_ascii) {
      return value.name_ascii;
    }
    if (value.name) {
      return value.name;
    }
    return value;
  }

  /**
   * Checks if a valid country value exists or not to enable/disable city
   */
  isCountryValueExists(value: Country) {
    return value && value.id ? null : '';
  }

  onSave() {
    let payload = {
      country_current: this.form.controls['currentCountry'].value.id
    };
    // check if city is custom or not
    let currentCity = this.form.controls['currentCity'].value;
    if (currentCity && currentCity.id) {
      // not custom
      payload['city_current'] = this.form.controls['currentCity'].value.id
    } else {
      // custom city
      payload['city_custom_current'] = this.form.controls['currentCity'].value
    }

    this.profileService.updateUserLocation(payload).subscribe((response) => {
      this.addSuccess.emit({
        currentCountry: this.form.controls['currentCountry'].value,
        currentCity: this.form.controls['currentCity'].value
      });
    }, (err) => {
      this.toastr.error('Unable to save current location', 'Error');
    })
  }

  /**
  * subscribes to value changes of current country
  */
  private _subscribeToCurrentCountryValueChanges() {
    const currentCountryControl = this.form.controls['currentCountry'];

    this.currentCountriesSearch = (text$: Observable<string>) =>
      text$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.form.controls['currentCity'].setValue('')),
        switchMap(term =>
          this.sharedService.getCountries(currentCountryControl.value)
        )
      )
  }

  /**
   * subscribes to value changes of current city
   */
  private _subscribeToCurrentCityValueChanges() {
    const currentCityControl = this.form.controls['currentCity'];
    this.currentCitiesSearch = (text$: Observable<string>) =>
      text$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
          let country = this.form.controls['currentCountry'].value;
          return this.sharedService.getCitiesByCountry(country.code, currentCityControl.value)
        })
      )
  }
}
